import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { Checkbox } from '@strata/react-checkbox-list';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { SecondaryData } from '@strata/react-grid';
import { StatusIndicator } from '@strata/react-status-indicators';
import { SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';

import { OrtfOutboundFileRowMenu } from './OrtfOutboundFileRowMenu';
import { OrtfOutboundGridSection } from './OrtfOutboundGridSection';

import { handleError } from 'services/Error';
import {
    useDownloadOrtfFileMutation,
    useGenerateOutboundFilesMutation,
    useGetOutboundFilesQuery,
    useGetProcessingFilesQuery,
    useGetRemainingTransfersQuery,
    useSendOutboundFilesMutation,
} from 'store/api/ndpApi';

const additionalFilesSubtitle =
    'Any pharmacies who are not enabled for ORTF or that would be receiving fewer than 25 outbound scripts';

const outboundSortDefaults = [{ id: 'pharmacy', desc: false }];
const additionalSortDefaults = [{ id: 'pharmacy', desc: false }];
const processingSortDefaults = [{ id: 'pharmacy', desc: false }];

const additionalColumns = [
    { accessorKey: 'pharmacy', header: 'Pharmacy', size: 220 },
    { accessorKey: 'prescriptions', header: 'Prescriptions', size: 140 },
    { accessorKey: 'ineligibilityReason', header: 'Ineligibility Reason', size: 280 },
];

const processingColumns = [
    { accessorKey: 'pharmacy', header: 'Pharmacy', size: 200 },
    { accessorKey: 'payer', header: 'Payer', size: 160 },
    { accessorKey: 'prescriptions', header: 'Prescriptions', size: 120 },
    { accessorKey: 'fileName', header: 'File Name', size: 200 },
];

export const OrtfOutboundFilesTab = () => {
    const [outboundFiles, setOutboundFiles] = useState();
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [hasGeneratedOutboundFiles, setHasGeneratedOutboundFiles] = useState(false);

    const [generateOutboundFiles, { isLoading: isGenerating }] = useGenerateOutboundFilesMutation();
    const {
        data: outboundFilesData,
        isFetching: isFetchingOutbound,
        refetch: refetchOutboundFiles,
    } = useGetOutboundFilesQuery();
    const {
        data: additionalFiles,
        isFetching: isFetchingRemaining,
        refetch: refetchRemainingTransfers,
    } = useGetRemainingTransfersQuery();
    const { data: processingFiles = [], isFetching: isFetchingProcessing } =
        useGetProcessingFilesQuery();
    const [sendOutboundFiles, { isLoading: isSending }] = useSendOutboundFilesMutation();
    const [downloadOrtfFile] = useDownloadOrtfFileMutation();

    const displayOutboundFiles = outboundFiles ?? outboundFilesData ?? [];
    const isLoadingOutboundFiles = isGenerating || isFetchingOutbound;

    const handleRefreshOutboundFiles = () => {
        setSelectedIds(new Set());
        setOutboundFiles(undefined);
        refetchOutboundFiles();
        refetchRemainingTransfers();
    };

    const handleGenerateOutboundFiles = async () => {
        const fileIds = displayOutboundFiles.map(({ id }) => id);
        try {
            await generateOutboundFiles({ fileIds }).unwrap();
            setOutboundFiles([]);
            setHasGeneratedOutboundFiles(true);
            setSelectedIds(new Set());
            SuccessToast({ message: 'Outbound file generation started.' });
        } catch (err) {
            handleError('Unable to generate outbound files. Please try again.', err);
        }
    };

    const handleDownloadFile = async ({ outboundFileId, fileName }) => {
        try {
            const blob = await downloadOrtfFile({ outboundFileId }).unwrap();
            const url = URL.createObjectURL(
                new File([blob], fileName, { type: blob.type || 'text/csv' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            handleError('Unable to download file. Please try again.', err);
        }
    };

    const handleSendOutboundFiles = async () => {
        const fileIds = [...selectedIds];
        try {
            await sendOutboundFiles({ fileIds }).unwrap();
            setOutboundFiles(displayOutboundFiles.filter((row) => !fileIds.includes(row.id)));
            setSelectedIds(new Set());
            SuccessToast({ message: 'Outbound files sent successfully.' });
        } catch (err) {
            handleError('Unable to send outbound files. Please try again.', err);
        }
    };

    const toggleReviewed = (rowId) => {
        setOutboundFiles((current) => {
            const rows = current ?? outboundFilesData;
            const row = rows.find(({ id }) => id === rowId);
            if (!row) {
                return current;
            }
            if (row.reviewed) {
                setSelectedIds((selected) => {
                    if (!selected.has(rowId)) {
                        return selected;
                    }
                    const next = new Set(selected);
                    next.delete(rowId);
                    return next;
                });
            }
            return rows.map((item) =>
                item.id === rowId ? { ...item, reviewed: !item.reviewed } : item,
            );
        });
    };

    const toggleRow = (rowId, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(rowId);
            } else {
                next.delete(rowId);
            }
            return next;
        });
    };

    const selectableOutboundIds = displayOutboundFiles
        .filter((row) => row.reviewed)
        .map((row) => row.id);

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? new Set(selectableOutboundIds) : new Set());
    };

    const allRowsSelected =
        selectableOutboundIds.length > 0 &&
        selectableOutboundIds.every((id) => selectedIds.has(id));
    const someRowsSelected =
        !allRowsSelected && selectableOutboundIds.some((id) => selectedIds.has(id));

    const outboundColumns = [
        {
            accessorKey: 'selected',
            enableSorting: false,
            header: () => (
                <div className="flex__gap--x-small" style={{ alignItems: 'center' }}>
                    <Checkbox
                        id="ortf-outbound-select-all"
                        inline
                        disabled={selectableOutboundIds.length === 0}
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={toggleSelectAll}
                    />
                </div>
            ),
            size: 48,
            cell: ({ row }) => {
                const { id, reviewed } = row.original;
                return (
                    <div className="flex__gap--x-small" style={{ alignItems: 'center' }}>
                        <Checkbox
                            id={`ortf-outbound-${id}`}
                            inline
                            disabled={!reviewed}
                            checked={reviewed && selectedIds.has(id)}
                            onChange={(checked) => reviewed && toggleRow(id, checked)}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'pharmacy',
            header: 'Pharmacy',
            size: 180,
        },
        {
            accessorKey: 'payer',
            header: 'Payer',
            size: 160,
        },
        {
            accessorKey: 'prescriptions',
            header: 'Prescriptions',
            size: 120,
        },
        {
            accessorKey: 'fileName',
            header: 'File Name',
            size: 200,
        },
        {
            accessorKey: 'createdDate',
            header: 'Created Date',
            size: 160,
            cell: ({ getValue }) => (
                <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
            ),
        },
        {
            accessorKey: 'reviewed',
            enableSorting: false,
            header: 'Reviewed',
            size: 130,
            cell: ({ row }) => {
                const { reviewed, id, fileName } = row.original;
                return (
                    <div
                        className="flex__gap--small"
                        style={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <StatusIndicator
                            message={reviewed ? 'Yes' : 'No'}
                            status={
                                reviewed
                                    ? StatusIndicator.Status.SUCCESS
                                    : StatusIndicator.Status.DEFAULT
                            }
                        />
                        <OrtfOutboundFileRowMenu
                            reviewed={reviewed}
                            onToggleReviewed={() => toggleReviewed(id)}
                            onDownload={() => handleDownloadFile({ outboundFileId: id, fileName })}
                        />
                    </div>
                );
            },
        },
    ];

    const isBusy = isLoadingOutboundFiles || isSending || isFetchingProcessing;
    const isLoadingTabData =
        (isFetchingOutbound || isFetchingRemaining || isFetchingProcessing) &&
        !isGenerating &&
        !isSending;

    return (
        <div className="margin__top--medium">
            <FullscreenLoading
                isShowing={isGenerating}
                loadingText="Requesting outbound file generation..."
            />
            <FullscreenLoading isShowing={isLoadingTabData} loadingText="Loading..." />
            <FullscreenLoading isShowing={isSending} loadingText="Sending outbound files..." />
            <OrtfOutboundGridSection
                actions={
                    <>
                        <Button
                            intent={Button.Intent.PRIMARY}
                            label="Generate Outbound Files"
                            disabled={isBusy || hasGeneratedOutboundFiles}
                            onClick={handleGenerateOutboundFiles}
                        />
                        <Button
                            intent={Button.Intent.NONE}
                            label="Send Outbound Files"
                            disabled={isBusy || selectedIds.size === 0}
                            onClick={handleSendOutboundFiles}
                        />
                    </>
                }
                columns={outboundColumns}
                data={displayOutboundFiles}
                onRefresh={handleRefreshOutboundFiles}
                sortProperties={outboundSortDefaults}
                title="Outbound Files"
                emptyMessage="No outbound files generated."
            />
            <OrtfOutboundGridSection
                className="margin__top--x-large"
                columns={additionalColumns}
                data={additionalFiles ?? []}
                sortProperties={additionalSortDefaults}
                subtitle={additionalFilesSubtitle}
                title="Remaining Transfers"
                emptyMessage="No remaining transfers available."
            />
            <OrtfOutboundGridSection
                className="margin__top--x-large"
                columns={processingColumns}
                data={processingFiles ?? []}
                sortProperties={processingSortDefaults}
                title="Processing Files"
                emptyMessage="No files are currently processing."
            />
        </div>
    );
};
